/**
 * React Query hooks for GENESIS API
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as genesisApi from '../services/genesisApi'
import type {
  RSApiStatus,
  RSTableSearchResult,
  RSTableMetadata,
  RSUploadResponse,
  RSImportExecuteRequest,
  RSImportResponse,
  RSImportStatusResponse,
  RSDataSourceInfo,
  RSExistingDataCheck,
  RSUnifiedSearchResponse,
  SearchParams,
  UnifiedSearchParams,
  GenesisInstance,
  GenesisInstanceInfo,
} from '../services/genesisApi'

// ============================================================================
// API Instances & Status
// ============================================================================

export function useGenesisInstances() {
  return useQuery<GenesisInstanceInfo[]>({
    queryKey: ['genesis', 'instances'],
    queryFn: genesisApi.getInstances,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  })
}

export function useRSApiStatus(instance: GenesisInstance = 'regionalstatistik') {
  return useQuery<RSApiStatus>({
    queryKey: ['genesis', 'api-status', instance],
    queryFn: () => genesisApi.getApiStatus(instance),
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  })
}

// ============================================================================
// Table Search
// ============================================================================

export function useRSTableSearch(params: SearchParams | null) {
  return useQuery<RSTableSearchResult[]>({
    queryKey: ['genesis', 'search', params],
    queryFn: () => genesisApi.searchTables(params!),
    enabled: params !== null && params.term.length >= 2,
    staleTime: 30 * 1000, // 30 seconds - fresh searches are important
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
  })
}

/**
 * Search tables across ALL configured GENESIS instances.
 * Returns results and status information per instance.
 */
export function useRSTableSearchAll(params: UnifiedSearchParams | null) {
  return useQuery<RSUnifiedSearchResponse>({
    queryKey: ['genesis', 'search-all', params],
    queryFn: () => genesisApi.searchTablesAll(params!),
    enabled: params !== null && params.term.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
  })
}

export function useRSTableMetadata(code: string | null, instance: GenesisInstance = 'regionalstatistik') {
  return useQuery<RSTableMetadata>({
    queryKey: ['genesis', 'table', code, instance],
    queryFn: () => genesisApi.getTableMetadata(code!, instance),
    enabled: code !== null,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

// ============================================================================
// Upload
// ============================================================================

export function useRSUpload() {
  return useMutation<RSUploadResponse, Error, File>({
    mutationFn: genesisApi.uploadFlatCSV,
  })
}

export function useRSUploadPreview(uploadId: string | null) {
  return useQuery<{
    upload_id: string
    summary: Record<string, unknown>
    sample_records: Record<string, unknown>[]
  }>({
    queryKey: ['regionalstatistik', 'upload', uploadId, 'preview'],
    queryFn: () => genesisApi.getUploadPreview(uploadId!),
    enabled: uploadId !== null,
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================================================
// Import Execution
// ============================================================================

export function useRSCheckExisting(tableCode: string | null, referenceYear: number | null) {
  return useQuery<RSExistingDataCheck>({
    queryKey: ['regionalstatistik', 'check-existing', tableCode, referenceYear],
    queryFn: () => genesisApi.checkExistingImport(tableCode!, referenceYear!),
    enabled: tableCode !== null && referenceYear !== null,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useRSExecuteImport() {
  const queryClient = useQueryClient()

  return useMutation<RSImportResponse, Error, RSImportExecuteRequest>({
    mutationFn: genesisApi.executeImport,
    onSuccess: () => {
      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['regionalstatistik', 'data'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useRSImportStatus(jobId: string | null, options?: { refetchInterval?: number | false }) {
  return useQuery<RSImportStatusResponse>({
    queryKey: ['regionalstatistik', 'import-status', jobId],
    queryFn: () => genesisApi.getImportStatus(jobId!),
    enabled: jobId !== null,
    refetchInterval: options?.refetchInterval ?? false,
  })
}

// ============================================================================
// Data Browser
// ============================================================================

export function useRSImportedData(params?: {
  table_code?: string
  limit?: number
  offset?: number
}) {
  return useQuery<RSDataSourceInfo[]>({
    queryKey: ['regionalstatistik', 'data', params],
    queryFn: () => genesisApi.getImportedData(params),
    staleTime: 60 * 1000,
  })
}
